import React, { useState, useEffect } from "react";
import { Container, Form, Row, Col } from "react-bootstrap";
import DynamicFormControl from "./DynamicFormControl";
import { useLang } from "../../common/hooks/useLang";
import axios from "../../common/api/HttpClient";
import { useTranslation } from "react-i18next";
import { NAMESPACE_REMOTE } from "../../common/translations/TranslationHelper";
import TransferHelper from "./TransferHelper";
import { useToken } from "../../common/hooks/useToken";
import { TOKEN_KEY_FIRM_ID } from "../../common/helpers/TokenHelper"

export default function AdditionalInfos({standardOrder, handleAdditionalInfosChanged, cachedOrder}) {
  const [additionalInfos, setAdditionalInfos] = useState([]);

  // The input fields for additional infos are controlled components,
  // but are dynamically rendered. So the form must be cleared manually.
  const clearForm = () => {
    document.getElementById("order-form")?.reset();
  };

  const { currentLanguage } = useLang();
  const { accessToken } = useToken();
  useEffect(() => {
    let notInterrupted = true;

    if (standardOrder !== null) {
      const userFirmId = accessToken.decodedToken[TOKEN_KEY_FIRM_ID];
      axios({
        method: "get",
        url: `/additional-info-order/${standardOrder["zusatzinfoId"]}`,
        params: { 
          firmaId: userFirmId,
          auftIdSauft: standardOrder["standardauftragId"],
          sprache: currentLanguage
        }
      })
        .then((response) => {
          if (notInterrupted) {
            // JSON.stringify + JSON.parse = object deep copy
            const initialAdditionalInfos = JSON.parse(
              JSON.stringify(
                response.data.data
              )
            );

            initialAdditionalInfos.forEach((p1) => {
              // Add additional property for the entered ui value
              p1["VALUE"] = null;

              // Add additional properties for domain parameters
              p1["DOMAIN_PARAM_1"] = null;
              p1["DOMAIN_PARAM_2"] = null;
              p1["DOMAIN_PARAM_3"] = null;
              p1["DOMAIN_PARAM_4"] = null;
              p1["DOMAIN_PARAM_5"] = null;
            });

            initialAdditionalInfos.forEach((p1) => {
              // Preset value optionally (and trigger field references)
              if (p1["VORBELEGUNG"] != null) {
                TransferHelper.presetAdditionalInfosByKey(
                  initialAdditionalInfos, p1["LAYO_ID"], p1["LAYO_VERSION"], p1["LAPR_ID"], p1["VORBELEGUNG"]
                );
              }
            });

            // Eventually restore values of previous order (and trigger field references)
            TransferHelper.presetAdditionalInfosByRequestData(initialAdditionalInfos, cachedOrder);

            clearForm();
            setAdditionalInfos(initialAdditionalInfos);
            handleAdditionalInfosChanged(initialAdditionalInfos);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }

    // If clean-up function executes, the axios call was interrupted
    // and the results can not be rendered in the dom anymore.
    // This may happen, if the user navigates back during the call.
    return () => {
      notInterrupted = false;
    }
  }, [standardOrder, accessToken.decodedToken, currentLanguage, cachedOrder, handleAdditionalInfosChanged]);

  const handleAdditionalInfoChanged = (layoId, layoVersion, laprId, newValue) => {
    const newAdditionalInfos = JSON.parse(JSON.stringify(additionalInfos));

    for (let additionalInfo of newAdditionalInfos) {
      TransferHelper.applyAdditionalInfoValue(additionalInfo, layoId, layoVersion, laprId, newValue);
    }

    setAdditionalInfos(newAdditionalInfos);
    handleAdditionalInfosChanged(newAdditionalInfos);
  };

  const { t } = useTranslation(NAMESPACE_REMOTE);

  if (standardOrder === null ||
      additionalInfos === null ||
      additionalInfos.filter((p) => p["REPT_KLASSE"] === "KWEB" || p["REPT_KLASSE"] === "KWUE").length === 0 ) {
    return null;
  } else {
    return (
      <Container fluid="md" className="pt-3 pb-2">
        <Form.Group as={Row}>
          <Col xs={12} sm={12}>
            <h2 className="h5">{t("TEXT.WSHOP2.CREATE_ORDER.HEADLINE_ADDITIONAL_INFOS_1")}</h2>
            <p className="text-muted fs-80">{t("TEXT.WSHOP2.CREATE_ORDER.HEADLINE_ADDITIONAL_INFOS_2")}</p>
          </Col>
        </Form.Group>

        <Form.Group>
            {additionalInfos.map((additionalInfo, index) => {
              if (additionalInfo["REPT_KLASSE"] === "KWEB") {
                return (
                  <Row className="mb-2" key={"key-" + standardOrder["standardauftragId"] + "-" 
                                                    + additionalInfo["LAYO_ID"] + "-"
                                                    + additionalInfo["LAYO_VERSION"] + "-" 
                                                    + additionalInfo["LAPR_ID"]}>
                    <Col xs={12} xl={9}>
                      <DynamicFormControl
                        additionalInfo={additionalInfo}
                        handleAdditionalInfoChanged={handleAdditionalInfoChanged}
                      />
                    </Col>
                  </Row>
                );
              } else if (additionalInfo["REPT_KLASSE"] === "KWUE") {
                return (
                  <Row className="mb-2" key={"key-" + standardOrder["standardauftragId"] + "-" 
                                                    + additionalInfo["LAYO_ID"] + "-"
                                                    + additionalInfo["LAYO_VERSION"] + "-" 
                                                    + additionalInfo["LAPR_ID"]}>
                    <Col xs={12} xl={9}>
                      <h6 className={index !== 0 ? 'mt-4 mb-2 fw-bold' : 'mb-2 fw-bold'}>{additionalInfo["PROMPT"]}</h6>
                    </Col>
                  </Row>
                );
              } else {
                return null;
              }
            })}
        </Form.Group>
      </Container>
    );
  }
}
